import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Sucursal } from "@/services/accounts.service";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { SuppliesOrderStatus } from "@/constants/entity-status";
import {
  fertSlotRequestOrderCreationSchema,
  FertSlotRequestOrderCreationForm,
} from "../order-creation/schema/fert-slot-request-order-creation.schema";
import { OrderCard } from "../order-creation/OrderCard";
import {
  buildDispatchPlaceOptions,
  buildProductOptions,
  fetchBranchesHandler,
  fetchProductsHandler,
  handleCancelHandler,
  handleDeleteOrderHandler,
  handleSaveAndCreateAnotherOrderHandler,
} from "../order-creation/schema/handler-functions";
import {
  fetchOrdersForEditHandler,
  updateOrdersHandler,
  OrderProduct,
  OrderMeta,
} from "./schema/handler-functions";
import { Save } from "lucide-react";
import { isEmpty } from "@/utils/query-string";
import { isValidHttpUrl, validateHttpUrl } from "@/utils/request-validations";

const normalizeOrderStatus = (status?: string) =>
  String(status ?? "")
    .trim()
    .toLowerCase();

const isLockedOrderStatus = (status?: string) => {
  const normalized = normalizeOrderStatus(status);

  return (
    normalized === normalizeOrderStatus(SuppliesOrderStatus.CONFIRMED) ||
    normalized === normalizeOrderStatus(SuppliesOrderStatus.IN_TRANSIT) ||
    normalized === "en camino" ||
    normalized === "en tránsito" ||
    normalized === normalizeOrderStatus(SuppliesOrderStatus.DELIVERED) ||
    normalized === normalizeOrderStatus(SuppliesOrderStatus.REJECTED) ||
    normalized === normalizeOrderStatus(SuppliesOrderStatus.NOT_DELIVERED)
  );
};

export const FertSlotRequestOrderEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number>(0);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingUpdateOrders, setLoadingUpdateOrders] = useState(false);

  const [fertilizantes, setFertilizantes] = useState<
    { id: number; nombre: string }[]
  >([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [orderProducts, setOrderProducts] = useState<OrderProduct[]>([]);
  const [ordersMeta, setOrdersMeta] = useState<OrderMeta[]>([]);

  const {
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<FertSlotRequestOrderCreationForm>({
    resolver: zodResolver(fertSlotRequestOrderCreationSchema),
    defaultValues: {
      orders: [],
    },
  });

  const watched = useWatch({ control });

  useEffect(() => {
    const orders = (watched?.orders ?? []) as any[];

    orders.forEach((o, idx) => {
      validateHttpUrl({
        watchedUrl: o?.URLMaps,
        clearErrors: clearErrors as any,
        setError: setError as any,
        fieldName: `orders.${idx}.URLMaps` as any,
      });
    });
  }, [watched?.orders]);

  const {
    fields: orderFields,
    append: appendOrder,
    remove: removeOrder,
  } = useFieldArray({
    control,
    name: "orders",
  });

  const confirmedOrderIds = useMemo(() => {
    return new Set(
      (ordersMeta ?? [])
        .filter(
          (o) => isLockedOrderStatus(o?.estado),
        )
        .map((o) => Number(o?.orderId ?? 0)),
    );
  }, [ordersMeta]);

  const hasNoFilledValues = useMemo(() => {
    const orders = (watched?.orders ?? []).filter(
      (o: any) => !confirmedOrderIds.has(Number(o?.orderId ?? 0)),
    );
    if (!orders.length) return true;

    const normalizeSingle = (v: any) => {
      if (Array.isArray(v)) return v?.[0];
      return v;
    };

    const parseTons = (v: any) => {
      const raw = typeof v === "string" ? v : String(v ?? "");
      const trimmed = raw.trim();
      if (!trimmed) return 0;
      const normalized = trimmed.replace(",", ".");
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return orders.some((o) => {
      const products = o?.products ?? [];
      const hasInvalidProducts =
        !products.length ||
        products.some((p: any) => {
          const productId = normalizeSingle(p?.productId);
          const tons = parseTons(p?.tons);
          return !productId || tons <= 0;
        });

      const dispatchPlace = normalizeSingle(o?.dispatchPlace);

      const url = String(normalizeSingle(o?.URLMaps) ?? "").trim();
      const invalidUrl = !isEmpty(url) && !isValidHttpUrl(url);

      return !dispatchPlace || hasInvalidProducts || isEmpty(url) || invalidUrl;
    });
  }, [watched, confirmedOrderIds]);

  const handleCancel = () => {
    handleCancelHandler(navigate, id);
  };

  const onSubmit = async (_values: FertSlotRequestOrderCreationForm) => {
    setLoadingUpdateOrders(true);
    try {
      await updateOrdersHandler({
        id,
        values: _values,
        navigate,
        fetchPermissions,
      });
    } finally {
      setLoadingUpdateOrders(false);
    }
  };

  const fetchProducts = async () => {
    await fetchProductsHandler({
      navigate,
      fetchPermissions,
      setLoadingProducts,
      setFertilizantes,
    });
  };

  const fetchBranches = async () => {
    await fetchBranchesHandler({
      navigate,
      fetchPermissions,
      setLoadingBranches,
      setSucursales,
    });
  };

  const fetchOrdersForEdit = async () => {
    await fetchOrdersForEditHandler({
      id,
      navigate,
      fetchPermissions,
      setLoadingOrders,
      reset,
      setOrderProducts,
      setOrdersMeta,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (!isActive) return;

    fetchProducts();
    fetchBranches();
    fetchOrdersForEdit();
  }, [isActive]);

  const productOptions = useMemo(() => {
    return buildProductOptions(fertilizantes, orderProducts);
  }, [fertilizantes, orderProducts]);

  const inactiveProductIds = useMemo(() => {
    return new Set(
      (productOptions ?? [])
        .filter((o: any) => Boolean(o?.isDisabled))
        .map((o: any) => String(o?.value ?? "")),
    );
  }, [productOptions]);

  const orderHasInactiveSelectedProduct = (orderIndex: number) => {
    const currentOrder = (watched?.orders as any)?.[orderIndex];
    const products = currentOrder?.products ?? [];
    return products.some((p: any) => {
      const raw = Array.isArray(p?.productId) ? p.productId?.[0] : p?.productId;
      const v = raw !== undefined && raw !== null ? String(raw) : "";
      return v && inactiveProductIds.has(v);
    });
  };

  const hasInactiveSelectedProductInEditableOrders = useMemo(() => {
    const orders = (watched?.orders as any) ?? [];
    return orders.some((_o: any, orderIndex: number) => {
      const orderId = Number(orders?.[orderIndex]?.orderId ?? 0);
      const readOnly = confirmedOrderIds.has(orderId);
      if (readOnly) return false;
      return orderHasInactiveSelectedProduct(orderIndex);
    });
  }, [watched?.orders, inactiveProductIds, confirmedOrderIds]);

  const dispatchPlaceOptions = useMemo(
    () => buildDispatchPlaceOptions(sucursales),
    [sucursales],
  );

  const handleDeleteOrder = (index: number) => {
    handleDeleteOrderHandler({
      index,
      removeOrder,
      setEditingIndex,
    });
  };

  const handleSaveAndCreateAnotherOrder = () => {
    handleSaveAndCreateAnotherOrderHandler({
      appendOrder,
      setEditingIndex,
      ordersLength: orderFields.length,
    });
  };

  const loading = loadingOrders || loadingProducts || loadingBranches;

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      height="100%"
      minHeight={0}
    >
      <SectionHeader
        title="Editar pedidos"
        addButtonLabel="Guardar cambios"
        addButtonIcon={<Save />}
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        addButtonType="submit"
        disableAdd={
          hasNoFilledValues ||
          loadingUpdateOrders ||
          hasInactiveSelectedProductInEditableOrders
        }
        loading={loading || loadingUpdateOrders}
      />

      {!loading && (
        <Flex
          flexDir="column"
          gap="16px"
          marginTop="30px"
          overflowY="auto"
          minH={0}
          flex="1"
          paddingRight="8px"
          paddingBottom="8px"
        >
          {orderFields.map((order, orderIndex) => {
            const orderId = Number((watched?.orders as any)?.[orderIndex]?.orderId ?? 0);
            const hasDriverAssigned = !!(watched?.orders as any)?.[orderIndex]?.chofer;
            const meta = (ordersMeta ?? []).find(
              (o) => Number(o?.orderId ?? 0) === orderId,
            );
            const orderStatus = meta?.estado;
            const hasAttempts = Boolean(meta?.hasAttempts);
            const readOnly =
              confirmedOrderIds.has(orderId) ||
              (orderStatus === SuppliesOrderStatus.PENDING && hasAttempts) ||
              hasDriverAssigned;

            const disableSaveAndCreateAnotherOrder =
              !readOnly && orderHasInactiveSelectedProduct(orderIndex);

            return (
              <OrderCard
                key={order.id}
                control={control as any}
                errors={errors as any}
                register={register as any}
                orderIndex={orderIndex}
                isEditing={editingIndex === orderIndex && !readOnly}
                setEditingIndex={setEditingIndex}
                onDeleteOrder={(index) => {
                  const deletingId = Number(
                    (watched?.orders as any)?.[index]?.orderId ?? 0,
                  );
                  if (confirmedOrderIds.has(deletingId)) return;
                  handleDeleteOrder(index);
                }}
                onSaveAndCreateAnotherOrder={handleSaveAndCreateAnotherOrder}
                disableSaveAndCreateAnotherOrder={disableSaveAndCreateAnotherOrder}
                productOptions={productOptions}
                dispatchPlaceOptions={dispatchPlaceOptions}
                loadingProducts={loadingProducts}
                loadingBranches={loadingBranches}
                orderFieldsLength={orderFields.length}
                watchedOrders={watched?.orders ?? []}
                readOnly={readOnly}
                orderStatus={orderStatus}
                hasAttempts={hasAttempts}
              />
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};
